import React, { useMemo } from 'react';

import { Alert, Button, Icon } from '@innovationnorway/cl-react';

import { HttpStatusCode } from 'src/api/v2';
import { useTranslation } from 'react-i18next';

export interface OperationResultListData {
  name: string;
  statusCode: HttpStatusCode;
  message: string;
}

type Props = {
  data: OperationResultListData[];
  className?: string;
};

const OperationResultList: React.FC<Props> = ({ data, className }) => {
  const { t: tCommon } = useTranslation();

  const [displayType, setDisplayType] = React.useState<'none' | 'success' | 'error' | 'all'>('none');

  const isSuccess = (status: HttpStatusCode): boolean => {
    switch (status) {
      case HttpStatusCode.OK:
      case HttpStatusCode.Created:
        return true;
      default:
        return false;
    }
  };

  const errors = useMemo(() => data.filter((x) => !isSuccess(x.statusCode)), [data]);
  const success = useMemo(() => data.filter((x) => isSuccess(x.statusCode)), [data]);
  const alertColor = useMemo<'success' | 'danger'>(
    () => (errors.length > 0 ? 'danger' : 'success'),
    [errors.length],
  );

  const displayRows = useMemo(() => {
    switch (displayType) {
      case 'success':
        return [...success];

      case 'error':
        return [...errors];

      case 'all':
        return [...data];

      default:
        return [];
    }
  }, [data, displayType, errors, success]);

  const renderMessage = (message: string) => {
    // TODO: Få en translation-key fra SLv2 her som vi kan bruke
    switch (message) {
      case 'Created':
        return tCommon('created');

      default:
        return message;
    }
  };

  if (data.length === 0) {
    return <></>;
  }

  return (
    <>
      <Alert data-color={alertColor} className={className}>
        <div>
          {tCommon('operationResultListDescription', { success: success.length, errors: errors.length })}
        </div>
        <div className="display--flex gap--2">
          <Button type="button" variant="tertiary" onClick={() => setDisplayType('error')}>
            {tCommon('showFailed')}
          </Button>

          <Button type="button" variant="tertiary" onClick={() => setDisplayType('success')}>
            {tCommon('showSucceeded')}
          </Button>

          <Button type="button" variant="tertiary" onClick={() => setDisplayType('all')}>
            {tCommon('showAll')}
          </Button>

          <Button type="button" variant="tertiary" onClick={() => setDisplayType('none')}>
            {tCommon('hide')}
          </Button>
        </div>
        {displayRows.length > 0 && (
          <>
            <hr />
            <div className="">
              {displayRows.map((x, index) => {
                return (
                  <div key={index} className="display--flex justify-content--space-between">
                    <div className="display--flex gap--2">
                      <span>
                        {isSuccess(x.statusCode) ? (
                          <Icon prefix="fasr" iconName="check" />
                        ) : (
                          <Icon prefix="fasr" iconName="times" />
                        )}
                      </span>
                      <span>{x.name}</span>
                    </div>

                    <span>{renderMessage(x.message)}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Alert>
    </>
  );
};

export default OperationResultList;
